import * as React from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { bindActionCreators, compose, Dispatch } from "redux";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ReactGA from "react-ga";

import SummaryPDF from "./SummaryPdf";
import * as ACTIONS from "./actionCreators";
import * as adminActionCreators from "../AdminDashboard/actionCreators";
import * as TransactionActionCreator from "../ThankYou/actionCreators";
import * as globalActionsCreator from "../../globalStore/actions";
import * as dynamicFormActionCreators from "../../components/DynamicForm/actionCreators";

import * as CONFIG from "../../../config.json";
import { getCustomerIDFromStore, getQueryString, setSessionValue, removeSessionBeforeUnload } from "../../utils/utils";
import { COPY_WARRANTY_DATA, CREATE_ANOTHER } from '../../constants/storageKeys';
import { RootState } from "../../rootReducer";
import IProps, { ThankYouProps, ThankYouActionProps } from "./IProps";

import "../../../assets/sass/components/thankYou.scss";

enum DownloadType {
    FullWarrantyPdf,
    SummaryPdf,
}

/**
 * @category Main Component
 * @class ThankYou
 * @description
 * Component will show after payment success
 * @param {IProps} props - Props of ThankYouComponent
 */
class ThankYouComponent extends React.Component<IProps, null> {
    componentDidMount() {
        const { fetchUserInfo, userInfo } = this.props;
        // fetch user info if not exist
        if (userInfo && Object.keys(userInfo).length > 0) {
            this.processValidateTransaction(true);
        } else if (!userInfo || Object.keys(userInfo).length === 0) {
            fetchUserInfo({ isFromThankyouPage: true, statusCode: this.getTransactionStatus() });
        }
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<null>, snapshot?: any) {
        const { transactionData, error, tempContractorSelection, userInfo } = this.props;

        if (prevProps.userInfo && Object.keys(prevProps.userInfo).length == 0 && Object.keys(userInfo).length > 0) {
            this.processValidateTransaction(false);
        }
        if (!prevProps.transactionData && transactionData) {
            this.initializeGA("/paymentSuccess");
        }
        if (!prevProps.tempContractorSelection && tempContractorSelection) {
            this.onTableRowClickConfirm(tempContractorSelection);
        }
        if (!prevProps.error && error) this.openDashboardPage();
    }

    /**
     * Process Validate Transaction
     */
    processValidateTransaction(makeonlyReviewData): void {
        const { transactionData, validateTransaction } = this.props;
        const paymentDirectorTransactionId = sessionStorage.getItem('paymentDirectorTransactionId');
        const id = paymentDirectorTransactionId ? null : this.getTransactionId();
        if (!transactionData && !makeonlyReviewData) {
            if (id) {
                validateTransaction({ id, status: this.getTransactionStatus(), isMakeValidateTransaction: true });
            } else {
                this.openDashboardPage();
            }
        } else if (transactionData && !id) {
            validateTransaction({
                id: transactionData.warrantyId.toString() || "",
                status: 0,
                isMakeValidateTransaction: false,
                warrantyNumber: transactionData.warrantyNumber
            });
        }
    }

    /**
     * getTransactionId from the `ref` query string param
     * @return {string} ref
     */
    getTransactionId = (): string => {
        //const param = new URLSearchParams(this.props.location.search);
        //return param.get("ref");
        const paymentDirectorTransactionId = sessionStorage.getItem('paymentDirectorTransactionId');
        if ( paymentDirectorTransactionId ) {
            return paymentDirectorTransactionId;
        } else {
            return getQueryString("ref", window.location.href);
        }
        
    };

    /**
     * getTransactionStatus
     * paymentsuccess: 1, paymenterror: 2, paymentcancel: 3
     * @return {number} status - either 1 / 2 / 3
     */
    getTransactionStatus = (): number => {
        const pathname = this.props.location.pathname;
        return pathname.includes("paymentsuccess") ? 1 : pathname.includes("paymentcancel") ? 3 : 2;
    };

    /**
     * initialize google analysis
     * @param {string} pageview
     */
    initializeGA = (pageview) => {
        ReactGA.initialize(CONFIG["CONFIG"].googleAnalyticsID);
        // ReactGA.pageview(pageview);
    };

    /**
     * Open dashboard page (DraftRegistration View)
     * @return {void}
     */
    openDashboardPage = (): void => {
        removeSessionBeforeUnload();
        if (this.props.cartId) {
            this.props.history.push("/myCart");
            return;
        }
        const customerId = getCustomerIDFromStore(false);
        const path = customerId ? `/?customerID=${customerId}` : "/";
        this.props.history.push(path);
        return;
    };

    onTableRowClickConfirm = (TableRowSelection) => {
        const { updateContractorSelection } = this.props;
        // Modify the contractor Selection as needed
        const contractorObject = {};
        contractorObject["activeStatus"] = TableRowSelection.isActive ? "Active" : "Inactive";
        contractorObject["contractorId"] = TableRowSelection.contractorId;
        contractorObject["contractorName"] = TableRowSelection.contractorName;
        contractorObject["customerId"] = TableRowSelection.customerId;
        const contractorAddress = TableRowSelection.addresses.filter((address) => {
            // address.addressType &&
            return address.addressType === "1" || address.addressType === null;
        });
        contractorObject["region"] = contractorAddress.length
            ? contractorAddress[0].city + ", " + contractorAddress[0].stateOrProvince
            : "N/A";
        updateContractorSelection(contractorObject);
        // this.props.history.push(`/?contractorId=${TableRowSelection.contractorId}`);
    };

    /**
     * Capture google analysis and open download pdf link
     * @param {DownloadType} downloadType
     */
    setGADownload = (downloadType: DownloadType) => {
        const { warrantyId } = this.props.transactionData;
        if (downloadType === DownloadType.FullWarrantyPdf) {
            ReactGA.event({
                category: "warranty confirmation",
                action: "warranty confirmation: download full warranty pdf",
                label: `warranty confirmation: download without warranty cost: ${warrantyId}`,
            });
        } else if (downloadType === DownloadType.SummaryPdf) {
            ReactGA.event({
                category: "warranty confirmation",
                action: "warranty confirmation: download summary pdf",
                label: `warranty confirmation: download: ${warrantyId}`,
            });
        }
    };

    /**
     * Download full warranty report
     * Fetch pdf from server
     */
    downloadFullPdf = (): void => {
        const { generateFullWarrantyPdf, transactionData } = this.props;
        const warrantynumber = transactionData.hasOwnProperty('warrantyNumber') ? transactionData.warrantyNumber : JSON.parse(sessionStorage.getItem('warrantynumber'));

        generateFullWarrantyPdf(transactionData.warrantyId, warrantynumber);
        this.setGADownload(DownloadType.FullWarrantyPdf);
    };

    downloadSummaryPdf = (): void => {
        const { downloadWarrantySummaryPdf, transactionData, reviewData } = this.props;
        const warrantynumber = transactionData.hasOwnProperty('warrantyNumber') ? transactionData.warrantyNumber : JSON.parse(sessionStorage.getItem('warrantynumber'));
        const fileName = `Warranty-Summary_${warrantynumber}.pdf`;
        downloadWarrantySummaryPdf(transactionData.warrantyId, warrantynumber)
        this.setGADownload(DownloadType.SummaryPdf);
    }

    componentWillUnmount() {
        this.props.clearTransactionData();
    }

    createNew = () => {
        const { userInfo } = this.props;
        setSessionValue(CREATE_ANOTHER, "true");
        const customerId = decodeURIComponent(getCustomerIDFromStore(true));
        let path = customerId ? `/?customerID=${customerId}` : "/";
        if (userInfo && userInfo.isAdmin) {
            path = "/?provider=okta";
        }
        window.location.replace(path);
    };

    copyMe = () => {
        setSessionValue(COPY_WARRANTY_DATA, "true");
        this.createNew();
    };
    onPrintReceiptClick = () => {
        let w = 400;
        let h = 400; // default sizes
        const popupWinWidth = 632;
        const popupWinHeight = 500;
        w = popupWinWidth + 30;
        h = popupWinHeight + 30;
        // window.open(`/receipt/${transactionId}`, "Payment Receipt", 'location=no,width=' + w + ',height=' + h + ',menubar=no,scrollbars=no,frame=false');
        this.popupWindow(
            `${CONFIG.CONFIG.pdReceiptUrl}/receipt/${this.getTransactionId()}`,
            "Payment Receipt",
            window,
            w,
            h
        );
    };
    popupWindow = (url, windowName, win, w, h) => {
        const y = ((win.top.outerHeight / 2) + win.top.screenY) - (h / 2);
        const x = ((win.top.outerWidth / 2) + win.top.screenX) - (w / 2);
        let width = w;
        let height = h;
        if (x < 0) {
            width = 1080;
            height = 800;
        }
        return win.open(
            url,
            windowName,
            `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`
        );
    };
    render() {
        const { userInfo, transactionData, reviewData } = this.props;
        const isAdmin = userInfo && userInfo.isAdmin;
        const isTm = userInfo && userInfo.isTm;
        const warrantynumber = transactionData && transactionData.hasOwnProperty('warrantyNumber') ? transactionData.warrantyNumber : (sessionStorage.getItem('warrantynumber'));
        return (
            <div className={`container ${isAdmin || isTm ? "admin-thank-you-container" : "thank-you-container"}`}>
                {transactionData && (
                    <div className="row">
                        <div className="text-center content">
                            <h1 className="thk-u-lbl">Thank You!</h1>
                            <div className="success-lbl mt-1 mb-2">Your registration has been submitted.</div>
                            <div className="container download mt-2">
                                <a
                                    className="col-md-6 col-sm-12 col-xs-12 download-btn"
                                    onClick={this.onPrintReceiptClick}
                                >
                                    PRINT RECEIPT
                                </a>
                            </div>
                            <div className="warranty">
                                <span className="lbl">Warranty number:</span> {warrantynumber}
                            </div>
                            <div className="mt-4 message">{transactionData.message}</div>
                            <div className="container download download-btn-with-icon">
                                {transactionData.canGenerateReport && (
                                    <a
                                        className="col-md-6 col-sm-12 col-xs-12 download-btn"
                                        onClick={this.downloadFullPdf}
                                    >
                                        Download full warranty pdf
                                    </a>
                                )}
                                {transactionData.canGenerateReport && (
                                    <a
                                        className="col-md-6 col-sm-12 col-xs-12 download-btn"
                                        onClick={this.downloadSummaryPdf}
                                    >
                                        Download summary pdf
                                    </a>
                                    // <PDFDownloadLink
                                    //     document={
                                    //         <SummaryPDF
                                    //             reviewData={reviewData}
                                    //             warrantyId={transactionData.warrantyId}
                                    //             warrantyNumber={warrantynumber}
                                    //         />
                                    //     }
                                    //     // fileName={"Warranty-Summary_" + transactionData.warrantyId + ".pdf"}
                                    //     fileName={"Warranty-Summary_" + warrantynumber + ".pdf"}
                                    //     className="col-md-6 col-sm-12 col-xs-12 download-btn"
                                    // >
                                    //     {({ blob, url, loading, error }) => "Download summary pdf"}
                                    // </PDFDownloadLink>
                                )}
                            </div>
                        </div>
                        <div className="create-copy-buttons">
                            <button
                                type="button"
                                tabIndex={10}
                                id="copy-warranty-button"
                                className="create-new copy-warranty-button"
                                onClick={this.copyMe}
                            >
                                Copy Warranty
                            </button>
                            <button
                                type="button"
                                tabIndex={10}
                                id="create-new-button"
                                className="create-new new-warranty-button"
                                onClick={this.createNew}
                            >
                                New Warranty
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: RootState): ThankYouProps => {
    return {
        userInfo: state.adminData.userInfo,
        error: state.payments.isTransactionFailed,
        transactionData: state.payments.transactionData,
        reviewData: state.dynamicFormInfo.reviewAndPayData,
        cartId: state.myCartInfo.cartId,
        tempContractorSelection: state.adminData.tempContractorSelection,
    };
};

const mapDispatchToProps = (dispatch: Dispatch): ThankYouActionProps => {
    return {
        fetchUserInfo: bindActionCreators(adminActionCreators.fetchUserInfo, dispatch),
        globalActions: bindActionCreators(globalActionsCreator, dispatch),
        updateContractorSelection: bindActionCreators(adminActionCreators.updateContractorSelection, dispatch),
        validateTransaction: bindActionCreators(ACTIONS.validateTransaction, dispatch),
        clearTransactionData: bindActionCreators(ACTIONS.clearTransactionData, dispatch),
        generateFullWarrantyPdf: bindActionCreators(ACTIONS.generateFullWarranty, dispatch),
        generateSummaryWarrantyPdf: bindActionCreators(ACTIONS.generateSummaryWarranty, dispatch),
        downloadWarrantySummaryPdf: bindActionCreators(ACTIONS.downloadSummaryPdf, dispatch),
        adminActions: bindActionCreators(adminActionCreators, dispatch),
        dynamicFormActions: bindActionCreators(dynamicFormActionCreators, dispatch),
        paymentActions: bindActionCreators(TransactionActionCreator, dispatch),
    };
};

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(ThankYouComponent);
